import React from "react";

import {
  GlobalMetaFieldDetails,
  GlobalMetaFieldListItemData,
  RecruitmentFormMetaFieldAvailable,
  ReduxError,
  WsExtension,
} from "src/types";
import { CreatorContentMonitor } from "src/types/creatorContentMonitoring";
import * as actionTypes from "./extensionTypes";
import { showToast } from "src/app/methods/showToast";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";

export interface ExtensionReducerState {
  errorMessages: ReduxError[];
  extensionList: WsExtension[];
  workspaceExtensionList: WsExtension[];
  isExtensionListLoading: boolean;
  isWorkspaceExtensionListLoading: boolean;
  isHowItWorksModalVisible: boolean;
  isMetaFieldMembersListLoading: boolean;
  globalMetaFieldList: GlobalMetaFieldListItemData[];
  isGlobalMetaFieldListLoading: boolean;
  globalMetaFieldDetails: GlobalMetaFieldDetails;
  availableProjectMetaFields: RecruitmentFormMetaFieldAvailable[];
  availableWorkspaceGlobalTaskMetaFields: RecruitmentFormMetaFieldAvailable[];
  creatorContentMonitors: CreatorContentMonitor[];
  newlyAddedMonitorUuid: string | null;
}

export const globalMetaFieldDetailsInitialState = {
  uuid: "",
  wsGlobalTaskMetaFieldUuid: null,
  members: [],
  name: "",
};

const initialState: ExtensionReducerState = {
  errorMessages: [],
  extensionList: [],
  workspaceExtensionList: [],
  isExtensionListLoading: false,
  isWorkspaceExtensionListLoading: false,
  isHowItWorksModalVisible: false,
  isMetaFieldMembersListLoading: false,
  globalMetaFieldList: [],
  isGlobalMetaFieldListLoading: false,
  globalMetaFieldDetails: globalMetaFieldDetailsInitialState,
  availableProjectMetaFields: [],
  availableWorkspaceGlobalTaskMetaFields: [],
  creatorContentMonitors: [],
  newlyAddedMonitorUuid: null,
};

// eslint-disable-next-line @typescript-eslint/default-param-last
function extensionReducer(state = initialState, action: any) {
  switch (action.type) {
    case actionTypes.GET_EXTENSION_LIST:
      return {
        ...state,
        isExtensionListLoading: true,
      };
    case actionTypes.GET_WORKSPACE_EXTENSION_LIST:
      return {
        ...state,
        isWorkspaceExtensionListLoading: true,
      };
    case actionTypes.GET_WORKSPACE_EXTENSION_LIST_SUCCESS:
      return {
        ...state,
        workspaceExtensionList: action.payload.data.content,
        isWorkspaceExtensionListLoading: false,
      };
    case actionTypes.GET_WORKSPACE_EXTENSION_LIST_FAIL:
      return {
        ...state,
        isExtensionListLoading: false,
      };
    case actionTypes.GET_EXTENSION_LIST_SUCCESS:
      return {
        ...state,
        extensionList: action.payload.data.content,
        isExtensionListLoading: false,
      };
    case actionTypes.GET_EXTENSION_LIST_FAIL:
      return {
        ...state,
        isExtensionListLoading: false,
      };
    case actionTypes.SET_EXTENSION_LIST:
      return {
        ...state,
        extensionList: action.payload.extensionList,
      };
    case actionTypes.SET_WORKSPACE_EXTENSIONS_LIST:
      return {
        ...state,
        workspaceExtensionList: action.payload.extensionList,
      };
    case actionTypes.CLEAR_EXTENSION_LIST:
      return {
        ...state,
        extensionList: [],
      };
    case actionTypes.SET_GLOBAL_TASK_META_VALUE_LIST:
      return {
        ...state,
        globalMetaFieldList: action.payload.globalMetaFieldList,
        isGlobalMetaFieldListLoading: false,
      };
    case actionTypes.SET_IS_GLOBAL_TASK_META_VALUE_LIST_LOADING:
      return {
        ...state,
        isGlobalMetaFieldListLoading:
          action.payload.isGlobalMetaFieldListLoading,
      };
    case actionTypes.GET_AVAILABLE_PROJECT_META_FIELDS_SUCCESS:
      return {
        ...state,
        availableProjectMetaFields: action.payload.data.content,
      };
    case actionTypes.GET_AVAILABLE_WORKSPACE_GLOBAL_TASK_META_FIELDS_SUCCESS:
      return {
        ...state,
        availableWorkspaceGlobalTaskMetaFields: action.payload.data.content,
      };
    case actionTypes.CLEAR_AVAILABLE_PROJECT_META_FIELDS:
      return {
        ...state,
        availableProjectMetaFields: [],
      };
    case actionTypes.CLEAR_AVAILABLE_WORKSPACE_GLOBAL_TASK_META_FIELDS:
      return {
        ...state,
        availableWorkspaceGlobalTaskMetaFields: [],
      };
    case actionTypes.GET_CREATOR_CONTENT_MONITORS_LIST_SUCCESS:
      return {
        ...state,
        creatorContentMonitors: action.payload.data.content,
      };

    case actionTypes.ADD_CREATOR_CONTENT_MONITOR_SUCCESS:
      return {
        ...state,
        newlyAddedMonitorUuid:
          action.payload.config.reduxSourceAction.payload.request.data.uuid,
      };

    case actionTypes.ADD_CREATOR_CONTENT_MONITOR_FAIL:
      return {
        ...state,
        errorMessages: action?.error?.response?.data?.errors || [],
        creatorContentMonitorLoading: false,
      };

    case actionTypes.CLEAR_NEWLY_ADDED_MONITOR_UUID:
      return {
        ...state,
        newlyAddedMonitorUuid: null,
      };

    case actionTypes.UPDATE_CREATOR_CONTENT_MONITOR:
      return {
        ...state,
        creatorContentMonitors: state.creatorContentMonitors.map((monitor) => {
          if (monitor.uuid === action.payload.request.data.uuid) {
            return action.payload.request.data;
          }
          return monitor;
        }),
      };

    case actionTypes.UPDATE_CREATOR_CONTENT_MONITOR_FAIL:
      return {
        ...state,
        errorMessages: action?.error?.response?.data?.errors || [],
      };

    case actionTypes.UPDATE_CREATOR_CONTENT_MONITOR_SUCCESS:
      showToast(
        "success",
        <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
        <IDHFormattedMessage
          id="ws_monitor_has_been_updated"
          defaultMessage="Monitor has been updated"
        />,
      );
      return state;

    case actionTypes.DELETE_CREATOR_CONTENT_MONITOR:
      return {
        ...state,
        creatorContentMonitors: state.creatorContentMonitors.filter(
          (monitor) => monitor.uuid !== action.payload.request.data.uuid,
        ),
      };

    default:
      return state;
  }
}

export default extensionReducer;
